/**
 * @desc The platform deployment's edge-gating decision, as a pure function (design D9, task 4.6).
 *
 * Lives apart from the rest of `src/lib` and is deliberately absent from its barrel: this module is
 * bundled by Vercel for the edge runtime via `apps/portal/middleware.ts`, not by Vite for the SPA.
 * That is also why it imports nothing — no `~api` alias, no `~lib` — since the edge bundler does
 * not resolve the workspace's TypeScript path aliases. The permission slug below is a
 * `User.permissions` value in `internal/api/src/model/user.model.ts`; keep them in step.
 *
 * The decision is defence-in-depth only. The cookie it reads is client-writable, so nothing here
 * may be trusted beyond a JWKS-verified signature, and the authoritative access control remains
 * the backend's permission gates (`internal/api/src/lib/context.ts`).
 */

/** @desc `User.permissions.PlatformAccess`. */
export const PLATFORM_ACCESS_PERMISSION = 'platform:access';

/** @desc `User.permissions.AdminAccess` — the Restricted section's gate (round 3, lane 11). */
export const ADMIN_ACCESS_PERMISSION = 'admin:access';

/** @desc The Restricted section's path prefix: `routes/_protected/restricted/**` in the SPA. */
export const RESTRICTED_PATH = '/restricted';

/** @desc The claims of interest from a verified WorkOS access token. */
export type EdgeTokenClaims = {
  permissions?: unknown;
  /** @desc WorkOS session id — consumed by the middleware's server-side logout, not the gate. */
  sid?: unknown;
};

export type EdgeDecision = 'serve' | 'no-access';

/** @desc What the gate needs to know about the request beyond who is asking. */
export type EdgeRequest = {
  pathname: string;
  /** @desc `VITE_RESTRICTED_NAV` as the edge environment sees it, parsed by {@link isFlagOn}. */
  restrictedNav: boolean;
};

/**
 * @desc The `VITE_RESTRICTED_NAV` parse, shared by the SPA (`env.ts`) and the edge so the two can
 * never disagree about whether the section exists. `"1"` is the documented spelling; `"true"` is
 * accepted too, because silently reading `true` as off is the worse failure. Anything else is off.
 */
export const isFlagOn = (value: string | undefined): boolean => value === '1' || value === 'true';

/**
 * @desc Whether `pathname` is inside the Restricted section. Compared the way the SPA router will
 * match it — case-insensitively (TanStack Router's default), percent-decoded, repeated slashes
 * collapsed — so a respelling of the path cannot reach the page while skipping this check.
 */
export function isRestrictedPath(pathname: string): boolean {
  let decoded = pathname;
  try {
    decoded = decodeURIComponent(pathname);
  } catch {
    // A malformed escape cannot route anywhere in the SPA either; compare it as sent.
  }
  const path = decoded.toLowerCase().replace(/\/{2,}/g, '/');
  return path === RESTRICTED_PATH || path.startsWith(`${RESTRICTED_PATH}/`);
}

const asStrings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

/**
 * @desc What to do with a request to the platform deployment.
 *
 * `claims` is `null` for every request the edge cannot positively identify — no cookie, a forged
 * or expired one, a JWKS fetch that failed. All of those serve the shell: a first-time or
 * signed-out visitor has to reach it to start the AuthKit flow, and an empty shell grants nothing
 * on its own. Only a validly signed token that demonstrably lacks platform access is turned away.
 *
 * The Restricted section (`/restricted/*`, staging-only and admin-only) inverts that default: it
 * is served only with the flag set in the edge environment AND a verified token carrying
 * `admin:access`. Anything less — including no token at all — is refused, since nobody needs to
 * start a sign-in from there. This only sees full page loads (in-app navigation never reaches the
 * edge), so it stays defence in depth: the SPA route guard and the admin-only procedures behind
 * any experiment's data are what actually hold the line.
 */
export function decideEdgeAccess(
  claims: EdgeTokenClaims | null,
  request: EdgeRequest,
): EdgeDecision {
  if (isRestrictedPath(request.pathname)) {
    if (!request.restrictedNav) return 'no-access';
    return asStrings(claims?.permissions).includes(ADMIN_ACCESS_PERMISSION) ? 'serve' : 'no-access';
  }

  if (!claims) return 'serve';

  if (asStrings(claims.permissions).includes(PLATFORM_ACCESS_PERMISSION)) return 'serve';

  return 'no-access';
}
